import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Activity, Clock, PhoneCall, Users, Zap, RefreshCw, TrendingUp } from "lucide-react";

// Flip Pilot Dialer Optimization V2 · Phase 2
// Supervisor / admin diagnostics — real P50/P75/P90/P95/P99 latency
// percentiles, outcome funnel, and conversation-utilization per caller.
// Every ms shown here is measured, never estimated.

const WINDOWS = [
  { key: 1,   label: "Last 1h" },
  { key: 8,   label: "Last 8h" },
  { key: 24,  label: "Last 24h" },
  { key: 168, label: "Last 7d" },
];

const LATENCY_ROWS = [
  { key: "next_call_latency",        label: "Post-call dead time (CONTROLLABLE)",
    target_p50: 1000, target_p95: 2000, critical: true },
  { key: "answer_to_media_latency",  label: "Answer → two-way audio",
    target_p50: 300,  target_p95: 750, critical: true },
  { key: "dial_api_latency",         label: "SignalWire dial API latency",
    target_p50: 500,  target_p95: 1500 },
  { key: "time_to_ring",             label: "Dial → first ring",
    target_p50: 2000, target_p95: 4000 },
  { key: "post_call_dead_time",      label: "Hangup → disposition saved",
    target_p50: 500,  target_p95: 1500 },
  { key: "transfer_manager_dial_latency", label: "Transfer click → manager dial",
    target_p50: 500,  target_p95: 1000 },
  { key: "transfer_manager_answer_latency", label: "Manager dial → manager answered",
    target_p50: 6000, target_p95: 15000 },
  { key: "transfer_join_latency",    label: "Manager answered → joined seller",
    target_p50: 500,  target_p95: 1500 },
];

function fmtMs(ms) {
  if (ms === null || ms === undefined) return "—";
  const n = Number(ms) || 0;
  if (n < 1000) return `${Math.round(n)}ms`;
  return `${(n / 1000).toFixed(2)}s`;
}

function fmtDurLong(ms) {
  if (!ms) return "0m";
  const s = Math.floor(ms / 1000);
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  if (h) return `${h}h ${m}m`;
  return `${m}m`;
}

function tone(v, target) {
  if (v === null || v === undefined) return "text-slate-400";
  if (!target) return "text-slate-700";
  if (v <= target * 0.7) return "text-emerald-600 font-bold";
  if (v <= target)       return "text-emerald-700";
  if (v <= target * 1.5) return "text-amber-600";
  return "text-red-600 font-bold";
}

export default function DialerPerformance() {
  const [hours, setHours]   = useState(24);
  const [data, setData]     = useState(null);
  const [busy, setBusy]     = useState(false);
  const [error, setError]   = useState("");

  const load = async () => {
    setBusy(true); setError("");
    try {
      const { data } = await api.get("/dialer/performance", { params: { hours } });
      setData(data);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load");
      setData(null);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [hours]);
  useEffect(() => {
    const t = setInterval(load, 30_000);
    return () => clearInterval(t);
    // eslint-disable-next-line
  }, [hours]);

  const totals   = data?.totals   || {};
  const rates    = data?.rates    || {};
  const talk     = data?.talk     || {};
  const lat      = data?.latencies || {};
  const callers  = data?.callers  || [];

  const isEmpty = !data || (totals.attempts || 0) === 0;

  return (
    <div className="p-6 space-y-6" data-testid="dialer-performance-page">
      {/* Header */}
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="text-2xl font-semibold flex items-center gap-2">
            <Activity className="text-fuchsia-500" size={24} />
            Dialer Performance
          </h1>
          <div className="text-xs text-muted-foreground mt-1">
            Measured latency percentiles + outcome funnel + conversation utilization.
            Every ms below is real — nothing estimated.
          </div>
        </div>
        <div className="flex items-center gap-2">
          <div className="flex rounded-full border-2 border-slate-200 overflow-hidden" data-testid="dialer-perf-window-picker">
            {WINDOWS.map((w) => (
              <button
                key={w.key}
                onClick={() => setHours(w.key)}
                data-testid={`dialer-perf-window-${w.key}`}
                className={`px-3 py-1.5 text-xs font-medium transition ${
                  hours === w.key
                    ? "bg-slate-900 text-white"
                    : "bg-white text-slate-700 hover:bg-slate-50"
                }`}
              >
                {w.label}
              </button>
            ))}
          </div>
          <Button
            variant="outline" size="sm"
            onClick={load} disabled={busy}
            data-testid="dialer-perf-refresh"
            className="rounded-full"
          >
            <RefreshCw size={14} className={`mr-1.5 ${busy ? "animate-spin" : ""}`} />
            Refresh
          </Button>
        </div>
      </div>

      {error && (
        <div className="rounded-lg border-2 border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* KPI strip */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="dialer-perf-kpis">
        <Kpi
          icon={<PhoneCall size={16} />}
          label="Attempts"
          value={totals.attempts || 0}
          sub={`${data?.attempts_per_hour || 0}/hr`}
          testid="kpi-attempts"
        />
        <Kpi
          icon={<Users size={16} />}
          label="Human Contacts"
          value={totals.HUMAN || 0}
          sub={`${rates.human_rate_pct || 0}% of attempts`}
          testid="kpi-human"
        />
        <Kpi
          icon={<Clock size={16} />}
          label="Total Talk Time"
          value={fmtDurLong(talk.total_ms)}
          sub={`avg ${fmtMs(talk.avg_ms)} / call`}
          testid="kpi-talk"
        />
        <Kpi
          icon={<Zap size={16} />}
          label="P95 Dead Time"
          value={fmtMs(lat.next_call_latency?.p95)}
          sub={`target <2s · P50 ${fmtMs(lat.next_call_latency?.p50)}`}
          testid="kpi-dead-time"
          highlight={
            lat.next_call_latency?.p95 !== null &&
            lat.next_call_latency?.p95 !== undefined &&
            lat.next_call_latency?.p95 <= 2000
          }
        />
      </div>

      {/* Latency percentile table */}
      <div className="rounded-2xl border-2 border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b bg-slate-50 flex items-center gap-2">
          <TrendingUp size={16} className="text-fuchsia-500" />
          <div className="text-sm font-semibold">Latency Percentiles</div>
          <div className="text-[11px] text-muted-foreground ml-2">
            green = at target · amber = degraded · red = missed. Percentiles &gt; averages for real-time systems.
          </div>
        </div>
        <div className="grid grid-cols-12 text-[10px] uppercase tracking-widest font-semibold text-slate-500 bg-slate-50/60 px-4 py-2 border-b">
          <div className="col-span-5">Metric</div>
          <div className="col-span-1 text-right">Count</div>
          <div className="col-span-1 text-right">Avg</div>
          <div className="col-span-1 text-right">P50</div>
          <div className="col-span-1 text-right">P75</div>
          <div className="col-span-1 text-right">P90</div>
          <div className="col-span-1 text-right">P95</div>
          <div className="col-span-1 text-right">P99</div>
        </div>
        {LATENCY_ROWS.map((row) => {
          const l = lat[row.key] || {};
          const p50Tone = tone(l.p50, row.target_p50);
          const p95Tone = tone(l.p95, row.target_p95);
          return (
            <div
              key={row.key}
              className="grid grid-cols-12 items-center px-4 py-2.5 border-b last:border-b-0 text-xs"
              data-testid={`dialer-perf-lat-${row.key}`}
            >
              <div className="col-span-5">
                <div className={`text-sm ${row.critical ? "font-semibold text-slate-800" : "text-slate-700"}`}>
                  {row.critical && <span className="mr-1 text-red-500">●</span>}
                  {row.label}
                </div>
                <div className="text-[10px] text-slate-400">
                  target: P50 &lt; {fmtMs(row.target_p50)} · P95 &lt; {fmtMs(row.target_p95)}
                </div>
              </div>
              <div className="col-span-1 text-right text-slate-500">{l.count || 0}</div>
              <div className="col-span-1 text-right text-slate-600">{fmtMs(l.avg)}</div>
              <div className={`col-span-1 text-right ${p50Tone}`}>{fmtMs(l.p50)}</div>
              <div className="col-span-1 text-right text-slate-600">{fmtMs(l.p75)}</div>
              <div className="col-span-1 text-right text-slate-600">{fmtMs(l.p90)}</div>
              <div className={`col-span-1 text-right ${p95Tone}`}>{fmtMs(l.p95)}</div>
              <div className="col-span-1 text-right text-slate-500">{fmtMs(l.p99)}</div>
            </div>
          );
        })}
        {isEmpty && (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            No calls in this window yet. Dial a few sellers and refresh — every timestamp will land here.
          </div>
        )}
      </div>

      {/* Outcome funnel */}
      <div className="rounded-2xl border-2 border-slate-200 bg-white shadow-sm overflow-hidden">
        <div className="px-4 py-3 border-b bg-slate-50 text-sm font-semibold">Outcome Funnel</div>
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3 p-4">
          <OutcomeCell label="Human"        value={totals.HUMAN         || 0} pct={rates.human_rate_pct}     tone="emerald" />
          <OutcomeCell label="Voicemail"    value={totals.VOICEMAIL     || 0} pct={rates.voicemail_rate_pct} tone="sky" />
          <OutcomeCell label="No Answer"    value={totals.NO_ANSWER     || 0} pct={rates.no_answer_rate_pct} tone="slate" />
          <OutcomeCell label="Busy"         value={totals.BUSY          || 0} pct={rates.busy_rate_pct}      tone="amber" />
          <OutcomeCell label="Failed/Invalid" value={(totals.FAILED || 0) + (totals.INVALID || 0)} pct={rates.failed_rate_pct} tone="red" />
          <OutcomeCell label="DNC Blocked"  value={totals.DNC_BLOCKED   || 0} pct={rates.dnc_block_pct}     tone="fuchsia" />
          <OutcomeCell label="Disconnected" value={totals.DISCONNECTED  || 0} pct={null} tone="slate" />
          <OutcomeCell label="Wrong Number" value={totals.WRONG_NUMBER  || 0} pct={null} tone="slate" />
        </div>
      </div>

      {/* Per-caller utilization */}
      <div className="rounded-2xl border-2 border-slate-200 bg-white shadow-sm overflow-hidden" data-testid="dialer-perf-callers">
        <div className="px-4 py-3 border-b bg-slate-50 text-sm font-semibold">
          Caller Productivity · Conversation Utilization %
        </div>
        <div className="grid grid-cols-12 text-[10px] uppercase tracking-widest font-semibold text-slate-500 bg-slate-50/60 px-4 py-2 border-b">
          <div className="col-span-3">Caller</div>
          <div className="col-span-1 text-right">Logged</div>
          <div className="col-span-1 text-right">Attempts</div>
          <div className="col-span-1 text-right">Contacts</div>
          <div className="col-span-1 text-right">Human</div>
          <div className="col-span-2 text-right">Talk Time</div>
          <div className="col-span-1 text-right">Xfers</div>
          <div className="col-span-2 text-right">Utilization</div>
        </div>
        {callers.length === 0 && (
          <div className="px-4 py-8 text-center text-sm text-muted-foreground">
            No caller activity in this window.
          </div>
        )}
        {callers.map((c) => (
          <div
            key={c.user_id}
            className="grid grid-cols-12 items-center px-4 py-2.5 border-b last:border-b-0 text-xs"
            data-testid={`dialer-perf-caller-${c.user_id}`}
          >
            <div className="col-span-3 font-medium text-slate-800 truncate">{c.name}</div>
            <div className="col-span-1 text-right text-slate-600">{fmtDurLong(c.login_ms)}</div>
            <div className="col-span-1 text-right text-slate-800 font-semibold">{c.attempts}</div>
            <div className="col-span-1 text-right text-slate-600">{c.contacts}</div>
            <div className="col-span-1 text-right text-emerald-600 font-semibold">{c.human}</div>
            <div className="col-span-2 text-right text-slate-700">{fmtDurLong(c.talk_ms)}</div>
            <div className="col-span-1 text-right text-fuchsia-600 font-bold">{c.transfers}</div>
            <div className="col-span-2 text-right">
              <UtilBadge pct={c.util_pct} />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


function Kpi({ icon, label, value, sub, testid, highlight }) {
  return (
    <div
      className={`rounded-xl border-2 p-4 shadow-sm ${
        highlight ? "border-emerald-300 bg-emerald-50/40" : "border-slate-200 bg-white"
      }`}
      data-testid={testid}
    >
      <div className="flex items-center gap-1.5 text-[10px] uppercase tracking-widest text-slate-500 font-bold">
        {icon}
        {label}
      </div>
      <div className="text-2xl font-bold text-slate-800 mt-1">{value}</div>
      <div className="text-[11px] text-slate-500 mt-0.5">{sub}</div>
    </div>
  );
}

function OutcomeCell({ label, value, pct, tone }) {
  const toneMap = {
    emerald: "bg-emerald-50 text-emerald-800 border-emerald-200",
    sky:     "bg-sky-50 text-sky-800 border-sky-200",
    slate:   "bg-slate-50 text-slate-700 border-slate-200",
    amber:   "bg-amber-50 text-amber-800 border-amber-200",
    red:     "bg-red-50 text-red-800 border-red-200",
    fuchsia: "bg-fuchsia-50 text-fuchsia-800 border-fuchsia-200",
  };
  return (
    <div className={`rounded-lg border-2 px-3 py-2 ${toneMap[tone] || toneMap.slate}`}>
      <div className="text-[10px] uppercase tracking-widest font-bold">{label}</div>
      <div className="text-xl font-bold mt-0.5">{value}</div>
      {pct !== null && pct !== undefined && (
        <div className="text-[10px] opacity-70">{pct}%</div>
      )}
    </div>
  );
}

function UtilBadge({ pct }) {
  const n = Number(pct) || 0;
  let tone = "bg-slate-100 text-slate-600 border-slate-200";
  if (n >= 30)      tone = "bg-emerald-100 text-emerald-800 border-emerald-300";
  else if (n >= 15) tone = "bg-amber-100 text-amber-800 border-amber-300";
  else              tone = "bg-slate-100 text-slate-600 border-slate-300";
  return (
    <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-[11px] font-bold border ${tone}`}>
      {n}%
    </span>
  );
}
